import { Camera, CameraResultType } from "@capacitor/camera";
import styled from "@emotion/styled";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { API } from "../../../../API";
import { usePermissionDeniedModal } from "../../../../hooks/usePermissionDeniedModal";
import { CaregiverMobile } from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { ARROW_RIGHT_SVG } from "../../../../utils/svgs";
import { CAMERA_ICON, PROFILE_ICON } from "../../../../utils/svgs_new";
import { AppIcon } from "../../../Generic/AppIcon";
import AsyncImage from "../../../Generic/AsyncImage";
import { IsDefined } from "../../../Generic/IsDefined";
import AuthForm, { AuthFormParagraph, AuthFormTitle } from "../AuthForm";
interface Props {
  api: API;
  caregiver: CaregiverMobile;
  onClickNext: () => void;
}

const TakePhotoStage = (props: Props) => {
  const [photoUrl, setPhotoUrl] = React.useState<string | null>(props.caregiver.photoUrl);
  const { openModal: openPermissionDeniedModal } = usePermissionDeniedModal();
  const getPhotoMutation = useMutation(async () => {
    const { camera: cameraPermission, photos: photosPermission } = await Camera.checkPermissions();

    if (cameraPermission === "denied") {
      openPermissionDeniedModal("camera");
      throw new Error("Camera permission denied");
    }

    if (photosPermission === "denied") {
      openPermissionDeniedModal("photos");
      throw new Error("Photos permission denied");
    }

    return Camera.getPhoto({ resultType: CameraResultType.Base64, width: 1024 });
  });

  const handleClickCamera = () =>
    getPhotoMutation.mutate(void 0, {
      onSuccess: ({ base64String, format }) => {
        if (base64String !== undefined) {
          const base64PhotoUrl = `data:image/${format};base64,${base64String}`;

          props.api.caregiverUploadPhoto(base64PhotoUrl);

          setPhotoUrl(base64PhotoUrl);
        }
      },
    });

  return (
    <TakePhotoStage.Styled as="div">
      <AuthFormTitle>{translate(STRINGS.TAKE_A_PHOTO)}:</AuthFormTitle>
      <AuthFormParagraph>
        {translate(STRINGS.PLEASE_SET_UP_YOUR_PROFILE_PHOTO_BY_CLICKING_ON_THE_CAMERA_ICON_BELOW)}
      </AuthFormParagraph>

      <TakePhotoStage.Placeholder onClick={handleClickCamera}>
        <IsDefined value={photoUrl}>
          {($photoUrl) => <AsyncImage className="avatar" src={$photoUrl} />}
        </IsDefined>
        <AppIcon size={72} icon={PROFILE_ICON} />
        <TakePhotoStage.Button onClick={handleClickCamera}>
          <AppIcon icon={CAMERA_ICON} />
        </TakePhotoStage.Button>
      </TakePhotoStage.Placeholder>

      <TakePhotoStage.NextButton onClick={props.onClickNext}>
        <AppIcon size={16} icon={ARROW_RIGHT_SVG} />
      </TakePhotoStage.NextButton>
    </TakePhotoStage.Styled>
  );
};

TakePhotoStage.NextButton = styled.button`
  --size: 64px;
  cursor: pointer;
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  border: none;
  background-color: var(--primary-soft-color);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 250ms ease;

  path {
    stroke: var(--primary-color);
  }

  &:hover {
    background-color: var(--primary-color);
    transform: scale(0.95);
    path {
      stroke: #fff;
    }
  }
`;

TakePhotoStage.Placeholder = styled.div`
  --size: 160px;
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  background-color: var(--gray-50);
  color: var(--gray-300);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  > .avatar {
    background-size: cover;
    position: absolute;
    top: 0;
    left: 0;
    width: var(--size);
    height: var(--size);
    border-radius: var(--size);
  }
`;

TakePhotoStage.Button = styled.button`
  --size: 48px;
  border: none;
  background-color: var(--primary-color);
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  position: absolute;
  bottom: 0;
  right: 0;

  .AppIcon {
    width: 24px;
  }
`;

TakePhotoStage.Styled = styled(AuthForm)`
  align-items: center;

  ${TakePhotoStage.Placeholder} {
    margin: 48px 0;
  }
`;

export default TakePhotoStage;
